import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import { isEventAdmin, rolesFromClaims } from "@/lib/member-roles";
import { safeReturnPath } from "@/lib/safe-return";
import { clearSessionRecovery } from "./session-recovery";

/**
 * Auth0 is the ONLY identity provider of this application.
 * Supabase Auth is never used for application authentication; the Auth0 ID token
 * is handed to Supabase Third-Party Auth, which verifies it (RS256) and applies RLS.
 *
 * Tokens are kept in memory only (cacheLocation "memory", no refresh tokens in
 * localStorage), so no token is persisted in browser storage.
 */
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN } from "./auth0-config";
export { AUTH0_CLIENT_ID, AUTH0_DOMAIN };

/** Module-level getter, registered by the client-side bridge below. */
let idTokenGetter: (() => Promise<string | null>) | null = null;
/** Waiters notified whenever the getter is registered or cleared. */
const getterWaiters = new Set<() => void>();

function setIdTokenGetter(getter: (() => Promise<string | null>) | null) {
  idTokenGetter = getter;
  getterWaiters.forEach((resolve) => resolve());
  getterWaiters.clear();
}

/** Used by the Supabase data client to attach the verified Auth0 identity. */
export async function getFcIdToken(): Promise<string | null> {
  if (!idTokenGetter) return null;
  try {
    return await idTokenGetter();
  } catch {
    return null;
  }
}

/**
 * Like getFcIdToken, but first waits briefly for the token bridge to register.
 * Needed on a fresh page load with a restored session: a route's effect can run
 * before the bridge's effect in the same commit, so the getter is not set yet.
 */
export async function getFcIdTokenWhenReady(timeoutMs = 3000): Promise<string | null> {
  if (!idTokenGetter) {
    await new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        getterWaiters.delete(done);
        resolve();
      }, timeoutMs);
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      getterWaiters.add(done);
    });
  }
  return getFcIdToken();
}

function TokenBridge({ children }: { children: ReactNode }) {
  const { isAuthenticated, getIdTokenClaims, getAccessTokenSilently } = useAuth0();
  const router = useRouter();

  useEffect(() => {
    router.options.context.documentSession.isAuthenticated = isAuthenticated;
    // Remove obsolete non-credential hints; never automatically redirect a
    // signed-out visitor to a failed silent-login callback.
    try { clearSessionRecovery(window.sessionStorage); } catch { /* Optional cleanup. */ }
  }, [isAuthenticated, router]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIdTokenGetter(null);
      return;
    }

    setIdTokenGetter(async () => {
      const claims = await getIdTokenClaims();
      const exp = typeof claims?.exp === "number" ? claims.exp : 0;
      // Refresh through Auth0 when the in-memory token is (nearly) expired.
      if (!claims?.__raw || exp * 1000 - Date.now() < 60_000) {
        try {
          await getAccessTokenSilently();
        } catch {
          return null;
        }
        const fresh = await getIdTokenClaims();
        return fresh?.__raw ?? null;
      }
      return claims.__raw;
    });

    return () => {
      setIdTokenGetter(null);
    };
  }, [isAuthenticated, getIdTokenClaims, getAccessTokenSilently]);

  return <>{children}</>;
}

/**
 * SSR-safe wrapper: the Auth0 SPA client requires browser APIs, so it is only
 * mounted after hydration. Before that, `useAuth0()` reports an unauthenticated,
 * loading state — no protected content is ever rendered server-side.
 *
 * The post-login destination travels inside Auth0's signed `state`
 * (appState), never as an open URL parameter, and is re-validated as a
 * same-origin relative path before navigating.
 */
export function FcAuthProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const router = useRouter();
  useEffect(() => setMounted(true), []);

  if (!mounted) return <>{children}</>;

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      cacheLocation="memory"
      useRefreshTokens={false}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/auth`,
        scope: "openid profile email",
      }}
      onRedirectCallback={(appState) => {
        // Official SDK invokes this only after validating the login callback.
        // Set before navigation so the clean /auth document stays alive.
        router.options.context.documentSession.isAuthenticated = true;
        router.history.replace(safeReturnPath(appState?.returnTo));
      }}
    >
      <TokenBridge>{children}</TokenBridge>
    </Auth0Provider>
  );
}

/** Starts Auth0 login directly and returns the user to `returnTo` (default: current page). */
export function useMemberLogin() {
  const { loginWithRedirect } = useAuth0();
  return (returnTo?: string) => {
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    void loginWithRedirect({
      appState: { returnTo: safeReturnPath(returnTo ?? current) },
      authorizationParams: { screen_hint: "login" },
    });
  };
}

/** Roles from the Auth0 token, for showing/hiding controls only. */
export function useMemberRoles() {
  const { user, isAuthenticated } = useAuth0();
  const roles = isAuthenticated ? rolesFromClaims(user as Record<string, unknown> | undefined) : [];
  return { roles, isAdmin: isEventAdmin(roles) };
}

export { useAuth0 };
